import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { css, useTheme } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import { SimpleCard } from '@hivebrite/design-system/SimpleCard';
import { useIsPageBottom } from '@hivebrite/react-hooks';

import ItemsContent from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/ItemsContent';
import LoadMoreButton from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/LoadMoreButton';
import TitleBlock from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/TitleBlock';
import {
  getFetchFilters,
  getItemsSize,
  getHasNoResult,
  getFetchNextPage,
  getScreenReaderStatus,
  getEffectiveSortBy,
} from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Items/utils';
import TagsFilters from 'hb-react/frontoffice/components/BlocksRenderer/blocks/Tags/TagsFilters';

import { useInfiniteItems } from './hooks/useInfiniteItems';
import { useTagsAndSort } from './hooks/useTagsAndSort';

const BlockItems = forwardRef(
  (
    {
      blockId,
      blockQuery,
      clearFilters,
      columnsInMobile,
      data,
      dataTestId,
      emptyState,
      fetchItems,
      getDataFilters,
      getDataTags,
      hideTitle,
      infiniteLoad,
      initialFetchFilters,
      initialPage,
      initialSort,
      itemLoader,
      itemRenderer,
      itemsByRow,
      itemsContentRenderer: ItemsContentRenderer,
      loaderLines,
      rowGap = 6,
      noResultsMessage,
      resultsFoundMessage,
      setBlockData,
      setBlockQuery,
      setScreenReaderStatus,
      shouldHideIfEmpty,
      sortBy,
      tags,
      title,
      titleRight,
      width,
      withCursorPagination,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isPageBottom = useIsPageBottom();

    const { selectedSort, selectedTags, handleChangeTags, handleChangeSort } =
      useTagsAndSort({
        blockId,
        blockQuery,
        setBlockQuery,
        sortBy,
      });

    const resetTags = () => {
      handleChangeTags(null);
    };

    const dataFilters = getDataFilters?.({ data });
    const dataTags = getDataTags?.({ data });
    const managesTagsInternally = Boolean(tags);

    const fetchFilters = getFetchFilters({
      dataFilters,
      initialFetchFilters,
      managesTagsInternally,
      selectedTags,
      dataTags,
      selectedSort,
      initialSort,
    });

    const {
      data: fetchedData,
      isFetching,
      isLoading,
      isFetchingNextPage,
      fetchNextPage,
      hasNextPage,
    } = useInfiniteItems({
      blockId,
      fetchFilters,
      fetchItems,
      initialPage,
      withCursorPagination,
    });

    const itemsSize = getItemsSize({ fetchedData });

    const hasNoResult = getHasNoResult({ isLoading, itemsSize });

    useEffect(() => {
      const status = getScreenReaderStatus({
        isFetching,
        isLoading,
        itemsSize,
        hasNoResult,
        resultsFoundMessage,
        noResultsMessage,
      });

      if (status !== null) {
        setScreenReaderStatus(status);
      }
    }, [
      hasNoResult,
      isFetching,
      isLoading,
      itemsSize,
      setScreenReaderStatus,
      resultsFoundMessage,
      noResultsMessage,
    ]);

    const handleClearFilters = () => clearFilters();

    useEffect(() => {
      const items = fetchedData?.pages
        .filter(Boolean)
        .reduce((acc, value) => [...acc, ...value.items], []);

      setBlockData(items);
    }, [fetchedData, setBlockData]);

    useEffect(() => {
      if (
        infiniteLoad &&
        isPageBottom &&
        hasNextPage &&
        !isFetchingNextPage &&
        !isLoading &&
        !isFetching
      ) {
        fetchNextPage();
      }
    }, [isPageBottom]);

    useImperativeHandle(ref, () => ({
      fetchNextPage: getFetchNextPage({ hasNextPage, fetchNextPage }),
      resetTags,
      checkHasTags: () => Boolean(selectedTags?.length),
    }));

    if (
      shouldHideIfEmpty &&
      hasNoResult &&
      !fetchFilters.filters &&
      !fetchFilters?.tags?.length
    ) {
      return null;
    }

    const effectiveSortBy = getEffectiveSortBy({
      isLoading,
      itemsSize,
      sortBy,
    });

    return (
      <>
        <Box
          display="block"
          css={css`
            > * + * {
              margin-top: ${theme.space[4]}px;
            }
          `}
          data-testid={dataTestId}
          id={blockId}
        >
          {tags?.position === 'before-title' && (
            <TagsFilters
              {...tags}
              selectedTags={selectedTags}
              onClick={handleChangeTags}
            />
          )}

          {!hideTitle && (
            <TitleBlock
              isLoading={isLoading}
              itemsSize={itemsSize}
              title={title}
              titleRight={titleRight}
              sortBy={effectiveSortBy}
              onChange={handleChangeSort}
              value={fetchFilters.sort}
              blockId={blockId}
              width={width}
            />
          )}

          {tags?.position === 'after-title' && (
            <TagsFilters
              {...tags}
              selectedTags={selectedTags}
              onClick={handleChangeTags}
            />
          )}
        </Box>

        <ItemsContentRenderer
          blockId={blockId}
          clearFilters={handleClearFilters}
          columnsInMobile={columnsInMobile}
          emptyState={emptyState}
          fetchedData={fetchedData}
          hasNoResult={hasNoResult}
          isLoading={isLoading}
          itemLoader={itemLoader}
          itemRenderer={itemRenderer}
          itemsByRow={itemsByRow}
          loaderLines={loaderLines}
          noResultsMessage={noResultsMessage}
          rowGap={rowGap}
          title={title}
        />

        {infiniteLoad && isFetchingNextPage && hasNextPage && (
          <Box pt={6} pb={6} data-testid="load-more">
            <Loader size={30} />
          </Box>
        )}
        {!infiniteLoad && !isLoading && hasNextPage && (
          <LoadMoreButton
            isLoading={isFetchingNextPage}
            handleLoadNewPage={fetchNextPage}
          />
        )}
      </>
    );
  },
);

BlockItems.defaultProps = {
  getDataFilters: ({ data }) => data?.['block-filters'], // See react/frontoffice/components/BlocksRenderer/shared.jsx
  getDataTags: ({ data }) => data?.['block-tags'], // See react/frontoffice/components/BlocksRenderer/shared.jsx
  hideTitle: false,
  infiniteLoad: true,
  initialPage: 1,
  itemsByRow: 4,
  itemsContentRenderer: ItemsContent,
  loaderLines: 3,
  shouldHideIfEmpty: false,
  withCursorPagination: false,
  title: ({ itemsSize, isLoading }) => (
    <>
      {__('Results')} {!isLoading && `(${itemsSize})`}
    </>
  ),
  emptyState: ({ clearFilters, noResultsMessage }) => (
    <SimpleCard
      faIconProps={{ iconName: 'megaphone' }}
      title={noResultsMessage ?? __('No matches found')}
      description={__(
        'Clear your filters or try again with different search criteria',
      )}
      buttonProps={{
        dataTestId: 'no-matches-filters-clear',
        onClick: clearFilters,
        size: 'large',
        children: __('Clear all filters'),
      }}
    />
  ),
};

export default BlockItems;
